import { useQuery } from '@apollo/client';
import {
  IColumn,
  IStackStyles,
  IconButton,
  Stack,
  Text,
  TooltipHost,
} from '@fluentui/react';
import {
  AttachablePayCycleDocuments,
  AttachablePayCycleDocumentsVariables,
  AttachablePayCycleDocuments_attachablePayCycleDocuments_nodes,
} from 'ap/paymentCycle/__generated__/AttachablePayCycleDocuments';
import { PayCycleAvailableDocumentTypes } from 'ap/paymentCycle/__generated__/PayCycleAvailableDocumentTypes';
import { GenericAttachForm } from 'common/components/ActionMenu/Attach/AttachFormModal';
import { AmountTextView } from 'common/components/AmountView/AmountTextView';
import { DocumentDataCallout } from 'common/components/AttachDocumentModal/DocumentDataCallout';
import { DocumentViewModalState } from 'common/components/DocumentList';
import {
  dateConvertions,
  dateFormat,
  getGlobalDateFormat,
} from 'common/utils/dateFormats';
import { useFormContext } from 'react-hook-form';
import React, { useCallback, useMemo } from 'react';
import { PayCycleValues } from '../types';
import { columns } from './column.data';
import { useStyles } from './index.styles';
import { toOrderByVariable } from './utils';

import ATTACHABLE_PAY_CYCLE_DOCUMENTS from '../../AttachablePayCycleDocuments.graphql';
import AVAILABLE_DOCUMENT_TYPES from '../../PayCycleAvailableDocumentTypes.graphql';

export type AttachableDataType =
  AttachablePayCycleDocuments_attachablePayCycleDocuments_nodes;

export interface attachedPaymentDocumentsData {
  attachedDocuments: AttachableDataType[];
}

interface AttachDocumentsFormProps {
  paymentAccountId?: string | null;
  currencyId?: number | null;
  departmentId?: string | null;
  cardHolderId?: string | null;
  onDocumentsAttached: (
    attachedDocumentsData: attachedPaymentDocumentsData
  ) => void;
  prevSelectedDocuments?: AttachableDataType[];
  previouslyAppliedAmount?: number;
  previousReceiptTotal?: string | null;
  onReceiptTotal?: (receiptTotal: string) => void;
}

const stackStyles: Partial<IStackStyles> = { root: { height: 44 } };

export const AttachDocumentsForm: React.FC<AttachDocumentsFormProps> = ({
  paymentAccountId,
  onDocumentsAttached,
  prevSelectedDocuments,
}) => {
  const { setValue } = useFormContext<PayCycleValues>();
  const styles = useStyles();

  const { data: documentTypesData } = useQuery<PayCycleAvailableDocumentTypes>(
    AVAILABLE_DOCUMENT_TYPES,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );

  const getDocumentTypeOptions = useCallback(
    (data: PayCycleAvailableDocumentTypes | undefined) => {
      return (
        data?.payCycleAvailableDocumentTypes?.nodes.map((doctype) => ({
          key: doctype.id,
          text: doctype.documentType || '',
        })) || []
      );
    },
    []
  );

  const transformData = useMemo(
    () => (data: AttachablePayCycleDocuments | undefined) => {
      const documents = data?.attachablePayCycleDocuments?.nodes.map(
        (document) =>
          ({
            ...document,
            available: document.documentAppliedAmounts?.remainingTotal,
            isoCode: document.currency?.isoCode,
          } as AttachableDataType)
      );
      if (prevSelectedDocuments?.length && documents?.length) {
        const filteredArray = documents.filter(
          ({ id: id1 }) =>
            !prevSelectedDocuments.some(({ id: id2 }) => id2 === id1)
        );
        return filteredArray;
      }
      return documents;
    },
    [prevSelectedDocuments]
  );

  const onRenderItemColumn = useCallback(
    (
      item?: AttachableDataType,
      _index?: number,
      column?: IColumn,
      openDocumentViewModal?: (state: DocumentViewModalState) => void
    ) => {
      if (!item || !column) return undefined;
      const fieldContent = item[
        column.fieldName as keyof AttachableDataType
      ] as string | null;

      switch (column.key) {
        case 'indexAmount':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <AmountTextView
                value={fieldContent}
                className={styles.contentColumnAlignRight}
              />
            </Stack>
          );
        case '_documentPoolName':
          return (
            <Stack
              horizontal
              className={styles.columnHeight}
              verticalAlign="center"
              tokens={{ childrenGap: 5 }}
            >
              <Text>{fieldContent}</Text>
            </Stack>
          );
        case 'fileReference':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <DocumentDataCallout item={item} />
            </Stack>
          );
        case '_uploadDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>{getGlobalDateFormat(item._uploadDate!)}</Text>
            </Stack>
          );
        case 'indexTransactionDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              {item.indexTransactionDate && (
                <Text>
                  {dateFormat(dateConvertions(item.indexTransactionDate))}
                </Text>
              )}
            </Stack>
          );
        case 'action':
          const viewDocumentVisible =
            item._isProtected! || item._fileViewer !== 'browser';
          return (
            <Stack
              className={styles.columnHeight}
              tokens={{ childrenGap: 10 }}
              horizontal
              verticalAlign="center"
            >
              <TooltipHost content="View" id="tooltipId">
                <IconButton
                  disabled={viewDocumentVisible}
                  iconProps={{ iconName: 'View' }}
                  onClick={() =>
                    openDocumentViewModal?.({
                      isOpen: true,
                      title: item.fileReference,
                      entityDocumentId: item.id,
                      _fileType: item._fileType!,
                    })
                  }
                />
              </TooltipHost>
            </Stack>
          );
        default:
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>{fieldContent}</Text>
            </Stack>
          );
      }
    },
    [styles]
  );

  const setTransactionFieldValues = useCallback(
    (fieldName: keyof PayCycleValues, fieldValue: any) => {
      setValue(fieldName, fieldValue, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [setValue]
  );

  const getMutationInput = useCallback(
    (selectedItems: AttachableDataType[]) => {
      return {
        attachedDocuments: selectedItems,
      } as attachedPaymentDocumentsData;
    },
    []
  );

  const onAttachedSuccessfully = useCallback(
    (selectedItems?: AttachableDataType[]) => {
      if (!selectedItems) return;

      const attachedDocuments = selectedItems.map(
        (item) =>
          ({
            ...item,
          } as AttachableDataType)
      );

      const populativeData = {
        ...attachedDocuments.filter((item) => item._isAccountingDocument)[0],
      };

      const { indexTransactionDate } = { ...populativeData };
      if (populativeData) {
        if (indexTransactionDate)
          setTransactionFieldValues(
            'defaultPaymentDate',
            dateFormat(dateConvertions(indexTransactionDate))
          );
        setTransactionFieldValues(
          'description',
          populativeData.indexDescription
        );
        setTransactionFieldValues(
          'controlTotalAmount',
          populativeData.indexAmount
        );
      }
      onDocumentsAttached({
        attachedDocuments,
      });
    },
    [setTransactionFieldValues, onDocumentsAttached]
  );
  return (
    <Stack styles={stackStyles} style={{ padding: '30px 20px' }}>
      <GenericAttachForm<
        AttachableDataType,
        AttachablePayCycleDocuments,
        AttachablePayCycleDocumentsVariables,
        unknown,
        attachedPaymentDocumentsData,
        PayCycleAvailableDocumentTypes
      >
        entity={{ id: paymentAccountId }}
        availableDocumentTypes={documentTypesData}
        attachableDocumentsQuery={ATTACHABLE_PAY_CYCLE_DOCUMENTS}
        detailsQuery={ATTACHABLE_PAY_CYCLE_DOCUMENTS} // Dummy, as not used
        columns={columns}
        modalWidth={1450}
        getDocumentTypeOptions={getDocumentTypeOptions}
        transformData={transformData}
        getMutationInput={getMutationInput}
        onRenderItemColumn={onRenderItemColumn}
        initialQueryVariables={{ paymentAccountId }}
        toOrderByVariable={toOrderByVariable}
        onAttachedSuccessfully={onAttachedSuccessfully}
        buttonRightAligned
      />
    </Stack>
  );
};